/**
 * Stock module actions — intakes, suppliers and ingredients.
 *
 * This replaces the legacy stock actions, which spoke to netlise-app-api's
 * `/stocks` and `/stock-record` routes. Those routes still exist and are
 * untouched; nothing in the dashboard calls them any more.
 *
 * Nothing here computes stock. Posting an intake is a CLOUD operation: the
 * lambda appends the purchase events and recomputes the caches, and what
 * comes back is what we store.
 *
 * See docs/feature-stock-intake-and-suppliers.md §7.1.
 */
import StockIntakeRepository from "../../internals/repository/StockIntakeRepository";
import StockItemRepository from "../../internals/repository/StockItemRepository";
import SupplierRepository from "../../internals/repository/SupplierRepository";

export const ModuleEvents = {
  // Intakes
  FETCH_INTAKES: "FETCH_INTAKES",
  FETCH_INTAKE: "FETCH_INTAKE",
  CREATE_INTAKE: "CREATE_INTAKE",
  UPDATE_INTAKE: "UPDATE_INTAKE",
  DELETE_INTAKE: "DELETE_INTAKE",
  POST_INTAKE: "POST_INTAKE",
  VOID_INTAKE: "VOID_INTAKE",
  CLEAR_SELECTED_INTAKE: "CLEAR_SELECTED_INTAKE",
  // Suppliers
  FETCH_SUPPLIERS: "FETCH_SUPPLIERS",
  CREATE_SUPPLIER: "CREATE_SUPPLIER",
  UPDATE_SUPPLIER: "UPDATE_SUPPLIER",
  DEACTIVATE_SUPPLIER: "DEACTIVATE_SUPPLIER",
  // Ingredients
  FETCH_INGREDIENTS: "FETCH_INGREDIENTS",
  CREATE_INGREDIENT: "CREATE_INGREDIENT",
  UPDATE_INGREDIENT: "UPDATE_INGREDIENT",
  DEACTIVATE_INGREDIENT: "DEACTIVATE_INGREDIENT",
};

const createAction =
  (type, action = () => {}, meta) =>
  (...args) => ({
    type,
    payload: action(...args),
    meta,
  });

/**
 * Repository calls reject on a non-2xx. An action that let that escape would
 * take the promise middleware's error path and show nothing useful, so each
 * one resolves to `{ error }` and lets the handler turn it into a message.
 */
const guard = (fn) => async (...args) => {
  try {
    return await fn(...args);
  } catch (e) {
    return { error: e?.message || "Request failed" };
  }
};

// ─── Intakes ─────────────────────────────────────────────────────────────────

const fetchIntakes = createAction(
  ModuleEvents.FETCH_INTAKES,
  guard(async (shopId, filters) => StockIntakeRepository.list(shopId, filters)),
  { loading: true },
);

const fetchIntake = createAction(
  ModuleEvents.FETCH_INTAKE,
  guard(async (shopId, intakeId) => ({
    intake: await StockIntakeRepository.get(shopId, intakeId),
  })),
  { loading: true },
);

const createIntake = createAction(
  ModuleEvents.CREATE_INTAKE,
  guard(async (shopId, input) => ({
    intake: await StockIntakeRepository.create(shopId, input),
  })),
  { loading: true },
);

const updateIntake = createAction(
  ModuleEvents.UPDATE_INTAKE,
  guard(async (shopId, intakeId, input) => ({
    intake: await StockIntakeRepository.update(shopId, intakeId, input),
  })),
  { loading: true },
);

const deleteIntake = createAction(
  ModuleEvents.DELETE_INTAKE,
  guard(async (shopId, intakeId) => {
    await StockIntakeRepository.remove(shopId, intakeId);
    return { intakeId };
  }),
  { loading: true },
);

const postIntake = createAction(
  ModuleEvents.POST_INTAKE,
  guard(async (shopId, intakeId, postedBy) =>
    StockIntakeRepository.post(shopId, intakeId, postedBy),
  ),
  { loading: true },
);

const voidIntake = createAction(
  ModuleEvents.VOID_INTAKE,
  guard(async (shopId, intakeId, reason, voidedBy) =>
    StockIntakeRepository.voidIntake(shopId, intakeId, reason, voidedBy),
  ),
  { loading: true },
);

const clearSelectedIntake = createAction(ModuleEvents.CLEAR_SELECTED_INTAKE, () => ({}));

// ─── Suppliers ───────────────────────────────────────────────────────────────

const fetchSuppliers = createAction(
  ModuleEvents.FETCH_SUPPLIERS,
  guard(async (shopId, q, includeInactive) => ({
    suppliers: await SupplierRepository.list(shopId, q, includeInactive),
  })),
);

const createSupplier = createAction(
  ModuleEvents.CREATE_SUPPLIER,
  guard(async (shopId, supplier) => ({
    supplier: await SupplierRepository.create(shopId, supplier),
  })),
  { loading: true },
);

const updateSupplier = createAction(
  ModuleEvents.UPDATE_SUPPLIER,
  guard(async (shopId, supplier) => ({
    supplier: await SupplierRepository.update(shopId, supplier),
  })),
  { loading: true },
);

const deactivateSupplier = createAction(
  ModuleEvents.DEACTIVATE_SUPPLIER,
  guard(async (shopId, supplierId) => ({
    supplier: await SupplierRepository.deactivate(shopId, supplierId),
  })),
  { loading: true },
);

// ─── Ingredients ─────────────────────────────────────────────────────────────

const fetchIngredients = createAction(
  ModuleEvents.FETCH_INGREDIENTS,
  guard(async (shopId, includeInactive) => ({
    ingredients: await StockItemRepository.list(shopId, includeInactive),
  })),
);

const createIngredient = createAction(
  ModuleEvents.CREATE_INGREDIENT,
  guard(async (shopId, item) => ({
    ingredient: await StockItemRepository.create(shopId, item),
  })),
  { loading: true },
);

const updateIngredient = createAction(
  ModuleEvents.UPDATE_INGREDIENT,
  guard(async (shopId, item) => ({
    ingredient: await StockItemRepository.update(shopId, item),
  })),
  { loading: true },
);

const deactivateIngredient = createAction(
  ModuleEvents.DEACTIVATE_INGREDIENT,
  guard(async (shopId, stockId) => ({
    ingredient: await StockItemRepository.deactivate(shopId, stockId),
  })),
  { loading: true },
);

const StockActions = {
  fetchIntakes,
  fetchIntake,
  createIntake,
  updateIntake,
  deleteIntake,
  postIntake,
  voidIntake,
  clearSelectedIntake,
  fetchSuppliers,
  createSupplier,
  updateSupplier,
  deactivateSupplier,
  fetchIngredients,
  createIngredient,
  updateIngredient,
  deactivateIngredient,
};

export default StockActions;
